import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  TrendingUp,
  Users,
  Award,
  PieChart,
  BarChart3,
  Activity,
  Monitor,
  ShoppingCart,
  Globe2,
  Target,
  Smile
} from 'lucide-react';

// Importar el servicio
import KpiService from '../../services/AGTUps-product-services/kpiService';

// ================================================
// Estilos y gradientes
// ================================================
const getAppleColors = (theme) => ({
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(28, 28, 30, 0.95)'
      : 'rgba(255, 255, 255, 0.95)',
  textPrimary:
    theme.palette.mode === 'dark'
      ? '#FFFFFF'
      : '#1D1D1F',
  textSecondary:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : 'rgba(29, 29, 31, 0.7)'
});

const appleGradients = {
  ingresosTotales: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  margenNeto: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  promedioTransaccion: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  flujoCaja: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  ventas: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  conversion: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  productosActivos: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  busquedas: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  visitasProductos: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  conversionBusqueda: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  chatsIniciados: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  negociacionesExitosas: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  tiempoCierre: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  marketShare: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  crecimientoIndustria: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  oportunidades: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  nuevosPerfiles: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  clientesActivos: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  satisfaccionCliente: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  }
};

// ================================================
// Tabla de iconos para mapear iconType => elemento
// ================================================
const iconsMap = {
  TrendingUp: <TrendingUp />,
  Users: <Users />,
  Award: <Award />,
  PieChart: <PieChart />,
  BarChart3: <BarChart3 />,
  Activity: <Activity />,
  Monitor: <Monitor />,
  ShoppingCart: <ShoppingCart />,
  Globe2: <Globe2 />,
  Target: <Target />,
  Smile: <Smile />
};

// ================================================
// Estilos de contenedores
// ================================================
const MetricsContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : getAppleColors(theme).background,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
  };
});

const MetricsInnerBox = styled(Box)(() => ({
  maxWidth: '1200px',
  width: '100%'
}));

const MetricsPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  background: 'transparent',
  boxShadow: 'none',
  color: getAppleColors(theme).textPrimary
}));

const MetricBox = styled(Box)(({ theme, bgKey }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const mode = theme.palette.mode;
  const gradient = appleGradients[bgKey]?.[mode === 'dark' ? 'dark' : 'light'];

  return {
    borderRadius: theme.spacing(2.5),
    padding: theme.spacing(3),
    background: isWallpaperMode ? 'rgba(128, 128, 128, 0.25)' : gradient,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  };
});

// Contenedor con scroll personalizado
const MetricsScrollContainer = styled(Box)(() => ({
  width: '100%',
  height: 'calc(100vh - 180px)', // Ajusta la altura según sea necesario
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  }
}));

// ================================================
// Componente principal
// ================================================
function Metrics() {
  const theme = useTheme();

  // Estados para KPIs y carga
  const [kpiSections, setKpiSections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Efecto para cargar los datos de KPIs al montar el componente
  useEffect(() => {
    async function loadKpis() {
      try {
        // Ya no pasamos userId, se usará el del token
        const data = await KpiService.getAllKpis();
        if (data && data.kpiSections) {
          setKpiSections(data.kpiSections);
        }
      } catch (error) {
        console.error('Error al cargar KPIs:', error);
      } finally {
        setLoading(false);
      }
    }
    loadKpis();
  }, []);

  // Render condicional según estado de carga
  if (loading) {
    return (
      <MetricsContainer>
        <MetricsInnerBox>
          <Typography variant="h6">Cargando KPIs...</Typography>
        </MetricsInnerBox>
      </MetricsContainer>
    );
  }

  if (!kpiSections || kpiSections.length === 0) {
    return (
      <MetricsContainer>
        <MetricsInnerBox>
          <Typography variant="h6">
            No hay datos de KPIs disponibles.
          </Typography>
        </MetricsInnerBox>
      </MetricsContainer>
    );
  }

  return (
    <MetricsContainer>
      <MetricsInnerBox>
        <MetricsScrollContainer>
          <MetricsPaper>
            <Typography
              variant="h2"
              sx={{
                mb: 1,
                fontWeight: 600,
                fontSize: '2.5rem',
                letterSpacing: '-0.5px'
              }}
            >
              Key Performance Indicators
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 6,
                color: getAppleColors(theme).textSecondary,
                fontSize: '1.1rem',
                fontWeight: 400
              }}
            >
              Resumen de tus principales indicadores de negocio
            </Typography>

            {kpiSections.map((section, sectionIndex) => (
              <Box key={sectionIndex} sx={{ mb: 10 }}>
                <Typography
                  variant="h4"
                  sx={{ mb: 1, fontWeight: 600, fontSize: '1.8rem' }}
                >
                  {section.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 4,
                    color: getAppleColors(theme).textSecondary,
                    fontSize: '1rem'
                  }}
                >
                  {section.description}
                </Typography>

                <Grid container spacing={3}>
                  {section.metrics.map((metric, idx) => {
                    // Convertir iconType a un elemento React real
                    const iconEl = iconsMap[metric.iconType] || <Activity />;

                    return (
                      <Grid item xs={12} sm={6} md={3} key={idx}>
                        <MetricBox bgKey={metric.key}>
                          {/* Título de la métrica */}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 500,
                              color: getAppleColors(theme).textSecondary,
                              mb: 0.5
                            }}
                          >
                            {metric.label}
                          </Typography>

                          {/* Descripción de la métrica */}
                          <Typography
                            variant="caption"
                            sx={{
                              color: getAppleColors(theme).textSecondary,
                              opacity: 0.8
                            }}
                          >
                            {metric.description}
                          </Typography>

                          {/* Valor e icono */}
                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'baseline' }}>
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: `${metric.iconColor}1A`,
                                mr: 1
                              }}
                            >
                              {React.cloneElement(iconEl, {
                                color: metric.iconColor
                              })}
                            </Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 500,
                                letterSpacing: '-1px'
                              }}
                            >
                              {metric.value}
                            </Typography>
                          </Box>
                        </MetricBox>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </MetricsPaper>
        </MetricsScrollContainer>
      </MetricsInnerBox>
    </MetricsContainer>
  );
}

export default Metrics;
